import { useEffect, useState } from "react";
import { HeartHandshake, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { fmtDue } from "@/components/dashboard/CourseWork";
import { speak } from "@/lib/buddy";

export const CatchUpCard = ({ studentId = "me", onDone }) => {
  const { buddy, profile } = useProfile();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => http.get(`/catchup/${studentId}`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d?.items.length) return null;
  const mine = profile.role === "student";
  const sfx = studentId === "me" ? "" : `-${studentId}`;
  const go = async () => {
    setBusy(true);
    try {
      const r = (await http.post(`/catchup/${studentId}`)).data;
      toast.success(`${r.moved} item${r.moved !== 1 ? "s" : ""} moved to ${fmtDue(r.next_day)}`);
      if (mine) speak(r.message, buddy?.voice).catch(() => {});
      await load(); onDone?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <section className="mt-10 rounded-3xl bg-[#EEF3EF] border border-[#C8DCCB] p-6 flex flex-wrap items-start gap-5 rise" data-testid={`catchup-card${sfx}`}>
      {mine && buddy ? <BuddyAvatar buddy={buddy} className="w-16 h-16 shrink-0" /> : <div className="w-12 h-12 rounded-full grid place-items-center bg-[#C8DCCB] text-[#2C4A3E]"><HeartHandshake className="w-6 h-6" /></div>}
      <div className="flex-1 min-w-[240px]">
        <p className="font-display text-2xl text-[#2C4A3E]">Catch-up with {d.buddy}</p>
        <p className="text-sm text-stone-600 mt-1" data-testid={`catchup-message${sfx}`}>{d.message}</p>
        <ul className="mt-3 flex flex-wrap gap-2">{d.items.map((i) => <li key={i.id} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#C8DCCB] text-[#2C4A3E]" data-testid={`catchup-item-${i.id}`}>{i.subject_short || i.subject_name}: {i.topic}</li>)}</ul>
        {d.missing_total > d.items.length && <p className="text-xs text-stone-500 mt-2">{d.missing_total - d.items.length} more missing. We'll take it a few at a time.</p>}
      </div>
      <button onClick={go} disabled={busy} data-testid={`catchup-btn${sfx}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C4A3E] text-white disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}Move to {fmtDue(d.next_day)}
      </button>
    </section>
  );
};
